import { useEffect, useState, type ReactNode } from 'react'

import {
  fetchQuote,
  type TransportOptions,
  type TripChoice,
} from '../../api/transport'
import { Button } from '../../components/Button'
import { PageBand, PageShell } from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { checkGhanaPhone } from '../../lib/ghanaPhone'
import { formatPesewas } from '../../lib/money'
import { useBooking, type Details, type DetailsDraft } from './booking'
import { Desk } from './Desk'
import { useFocusOnMount } from './focus'
import { ProgressBar } from './ProgressBar'
import { MOBILITY } from './services'
import { TransportHeader } from './TransportHeader'

/**
 * Screen 4, passenger and needs (M39-T12, spec §C.3.4). Who is booking, who
 * travels, and what they need on the way. No medical information is asked
 * for (Act 843, data minimisation), and no email (owner Q12): the phone is
 * for the MoMo prompt and booking SMS.
 *
 * The page checks what it can (the phone's shape, the adult rule, the
 * caregiver a service needs); the checkout checks it all again (M39-T04a).
 */

const field =
  'mt-1 w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-[14px] text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:opacity-50'
const label = 'text-[13px] font-medium text-ink-1'
const legend = 'font-mono text-[10px] uppercase tracking-wide text-ink-3'
const control =
  'h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const choice =
  'flex items-start gap-2.5 rounded-r3 border border-line-2 px-3 py-2.5 text-[13px] text-ink-1'

const CONSENT =
  "I have the passenger's permission to share these details with Medcor, the driver and the hospital."

type Wheelchair = 'none' | 'own' | 'needed'

/** The supplied wheelchair's price, from a fresh quote with one added. */
type WheelchairPrice =
  | { kind: 'loading' }
  | { kind: 'priced'; pesewas: number }
  | { kind: 'not_online' }
  | { kind: 'unknown' }

const name = (v: string, max: number) => {
  const t = v.trim()
  return t.length >= 1 && t.length <= max
}

export function PassengerScreen({
  options,
  onBack,
  onEditMobility,
  onStop,
  onContinue,
}: {
  options: TransportOptions
  onBack: () => void
  /** "Edit" on the mobility summary: back to Screen 1. */
  onEditMobility: () => void
  /** 112 from this screen ends the booking (not counted: only the gate is). */
  onStop: () => void
  onContinue: () => void
}): ReactNode {
  const { booking, update } = useBooking()
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const service = booking.service ?? 'standard_ride'
  const usesOxygen = booking.usesOxygen === true
  // What was typed when the person last left this screen, else the answers
  // last saved by "Review fare", else nothing.
  const start = booking.detailsDraft ?? fromDetails(booking.details)

  const [first, setFirst] = useState(start.first)
  const [last, setLast] = useState(start.last)
  const [phone, setPhone] = useState(start.phone)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [someoneElse, setSomeoneElse] = useState(start.someoneElse)
  const [passengerName, setPassengerName] = useState(start.passengerName)
  const [age, setAge] = useState(start.age)
  const [passengerPhone, setPassengerPhone] = useState(start.passengerPhone)
  const [consent, setConsent] = useState(start.consent)
  const [wheelchair, setWheelchair] = useState<Wheelchair>(start.wheelchair)
  const [ownOxygen, setOwnOxygen] = useState(start.ownOxygen)
  const [companion, setCompanion] = useState<'none' | 'caregiver'>(
    start.companion,
  )
  const [notes, setNotes] = useState(start.notes)
  const [price, setPrice] = useState<WheelchairPrice>({ kind: 'loading' })

  // The supplied wheelchair's price: the same trip, quoted with one. A
  // `tariff_missing` here (the trip itself priced on Screen 3) means the
  // wheelchair fare is not set, so it cannot be booked online.
  const trip = booking.trip
  useEffect(() => {
    if (!trip) return
    const body: TripChoice = {
      service,
      pickup_area_id: trip.pickupAreaId,
      hospital_id: trip.hospitalId,
      ...(trip.hireLength
        ? { hire_length: trip.hireLength }
        : { journey: trip.journey ?? 'one_way' }),
      wheelchair_needed: true,
    }
    const ctrl = new AbortController()
    fetchQuote(body, ctrl.signal).then(
      (q) => {
        const line = q.lines?.find((l) => l.item === 'wheelchair')
        if (q.bookable && line) {
          setPrice({ kind: 'priced', pesewas: line.amount_pesewas })
        } else if (q.reason === 'tariff_missing') {
          setPrice({ kind: 'not_online' })
        } else {
          setPrice({ kind: 'unknown' })
        }
      },
      () => {
        if (!ctrl.signal.aborted) setPrice({ kind: 'unknown' })
      },
    )
    return () => ctrl.abort()
  }, [service, trip])

  const phoneCheck = checkGhanaPhone(phone)
  const passengerPhoneCheck = passengerPhone.trim()
    ? checkGhanaPhone(passengerPhone)
    : null
  const ageNumber = /^\d{1,3}$/.test(age.trim()) ? Number(age.trim()) : null
  const ageValid = ageNumber !== null && ageNumber <= 120
  const minAge = options.min_unaccompanied_age
  const tooYoung = someoneElse && ageValid && ageNumber < minAge
  const row = options.eligibility.find(
    (r) =>
      r.mobility === booking.mobility && r.uses_oxygen === booking.usesOxygen,
  )
  const serviceNeedsCaregiver =
    row?.services[service]?.state === 'needs_caregiver'
  const seats = options.caregiver_seats
  const caregiverRequired = tooYoung || serviceNeedsCaregiver
  const caregiverMissing = caregiverRequired && companion !== 'caregiver'
  const wheelchairBlocked =
    wheelchair === 'needed' && price.kind === 'not_online'

  const missing = [
    !name(first, 60) && 'first name',
    !name(last, 60) && 'last name',
    !phoneCheck.ok && 'phone',
    someoneElse && !name(passengerName, 120) && "passenger's name",
    someoneElse && !ageValid && "passenger's age",
    someoneElse &&
      passengerPhoneCheck !== null &&
      !passengerPhoneCheck.ok &&
      "passenger's phone",
    someoneElse && !consent && 'permission to share their details',
    usesOxygen && !ownOxygen && 'portable oxygen',
    caregiverMissing && 'family or caregiver',
    wheelchairBlocked &&
      "another wheelchair choice (we can't supply one online)",
  ].filter((m): m is string => typeof m === 'string')
  const canContinue = missing.length === 0

  // Leaving unfinished keeps what was typed (Back, Edit mobility).
  const draft: DetailsDraft = {
    first,
    last,
    phone,
    someoneElse,
    passengerName,
    age,
    passengerPhone,
    consent,
    wheelchair,
    ownOxygen,
    companion,
    notes,
  }
  const leave = (go: () => void) => () => {
    update({ detailsDraft: draft })
    go()
  }

  const mobilityLabel =
    MOBILITY.find((m) => m.value === booking.mobility)?.label ?? 'Not given'

  const save = () => {
    if (!phoneCheck.ok) return
    const details: Details = {
      bookerFirstName: first.trim(),
      bookerLastName: last.trim(),
      bookerPhone: phoneCheck.normalised,
      passengerIsBooker: !someoneElse,
      passengerName: someoneElse ? passengerName.trim() : null,
      passengerAge: someoneElse ? ageNumber : null,
      passengerPhone:
        someoneElse && passengerPhoneCheck?.ok
          ? passengerPhoneCheck.normalised
          : null,
      consentGiven: someoneElse && consent,
      ownWheelchair: wheelchair === 'own',
      wheelchairNeeded: wheelchair === 'needed',
      ownOxygen: usesOxygen && ownOxygen,
      companion,
      driverNotes: notes.trim() || null,
    }
    update({ details, detailsDraft: draft })
    onContinue()
  }

  return (
    <PageShell measure={640} as="main">
      <TransportHeader onBack={leave(onBack)} />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Passenger and needs
        </h1>
        <ProgressBar current={3} />

        <section aria-labelledby="pass-booker" className="flex flex-col gap-3">
          <h2 id="pass-booker" className={legend}>
            Who is booking
          </h2>
          <div className="flex gap-2">
            <label className={`${label} flex-1`}>
              First name
              <input
                className={field}
                required
                maxLength={60}
                autoComplete="given-name"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
              />
            </label>
            <label className={`${label} flex-1`}>
              Last name
              <input
                className={field}
                required
                maxLength={60}
                autoComplete="family-name"
                value={last}
                onChange={(e) => setLast(e.target.value)}
              />
            </label>
          </div>
          <label className={label}>
            Phone (MoMo / SMS)
            <input
              className={field}
              required
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              aria-invalid={phoneTouched && !phoneCheck.ok}
              aria-describedby="pass-phone-help"
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
            />
          </label>
          <p
            id="pass-phone-help"
            className={`-mt-2 text-[12px] ${
              phoneTouched && !phoneCheck.ok ? 'text-sos' : 'text-ink-3'
            }`}
          >
            {phoneTouched && !phoneCheck.ok
              ? phoneCheck.message
              : 'For the payment prompt and your booking messages.'}
          </p>
        </section>

        <fieldset className="flex flex-col gap-2">
          <legend className={legend}>Who is travelling?</legend>
          <div className="mt-2 flex gap-2">
            {(
              [
                [false, 'Me'],
                [true, 'Someone else'],
              ] as const
            ).map(([value, text]) => (
              <label key={text} className={`${choice} flex-1 items-center`}>
                <input
                  type="radio"
                  name="who"
                  className={control}
                  checked={someoneElse === value}
                  onChange={() => setSomeoneElse(value)}
                />
                <span className="font-medium">{text}</span>
              </label>
            ))}
          </div>
          {someoneElse && (
            <div className="mt-1 flex flex-col gap-3">
              <label className={label}>
                Passenger&apos;s name
                <input
                  className={field}
                  required
                  maxLength={120}
                  value={passengerName}
                  onChange={(e) => setPassengerName(e.target.value)}
                />
              </label>
              <label className={label}>
                Passenger&apos;s age
                <input
                  className={field}
                  required
                  inputMode="numeric"
                  maxLength={3}
                  value={age}
                  aria-invalid={age.trim() !== '' && !ageValid}
                  aria-describedby="pass-age-help"
                  onChange={(e) => setAge(e.target.value)}
                />
              </label>
              <p
                id="pass-age-help"
                className={`-mt-2 text-[12px] ${
                  age.trim() !== '' && !ageValid ? 'text-sos' : 'text-ink-3'
                }`}
              >
                In years, from 0 to 120.
              </p>
              <label className={label}>
                Passenger&apos;s phone (optional)
                <input
                  className={field}
                  type="tel"
                  inputMode="tel"
                  value={passengerPhone}
                  aria-invalid={
                    passengerPhoneCheck !== null && !passengerPhoneCheck.ok
                  }
                  aria-describedby="pass-passenger-phone-help"
                  onChange={(e) => setPassengerPhone(e.target.value)}
                />
              </label>
              <p
                id="pass-passenger-phone-help"
                className={`-mt-2 text-[12px] ${
                  passengerPhoneCheck?.ok === false ? 'text-sos' : 'text-ink-3'
                }`}
              >
                {passengerPhoneCheck?.ok === false
                  ? passengerPhoneCheck.message
                  : 'So the driver can reach them on the day.'}
              </p>
              <label className={choice}>
                <input
                  type="checkbox"
                  className={`${control} mt-0.5`}
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>{CONSENT}</span>
              </label>
            </div>
          )}
        </fieldset>

        <div className="flex items-start justify-between gap-3 rounded-r3 border border-line-1 bg-bg-soft px-3 py-2.5">
          <p className="text-[13px] text-ink-1">
            <span className="block text-[11.5px] text-ink-3">Mobility</span>
            {mobilityLabel} · {usesOxygen ? 'Uses oxygen' : 'No oxygen'}
          </p>
          <button
            type="button"
            onClick={leave(onEditMobility)}
            className="text-[12.5px] font-medium text-accent underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Edit<span className="sr-only"> mobility</span>
          </button>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className={legend}>Equipment</legend>
          <div className="mt-2 flex flex-col gap-2">
            <label className={choice}>
              <input
                type="radio"
                name="wheelchair"
                className={`${control} mt-0.5`}
                checked={wheelchair === 'none'}
                onChange={() => setWheelchair('none')}
              />
              <span className="font-medium">No wheelchair</span>
            </label>
            <label className={choice}>
              <input
                type="radio"
                name="wheelchair"
                className={`${control} mt-0.5`}
                checked={wheelchair === 'own'}
                onChange={() => setWheelchair('own')}
              />
              <span className="font-medium">Own wheelchair</span>
            </label>
            <label className={choice}>
              <input
                type="radio"
                name="wheelchair"
                className={`${control} mt-0.5`}
                checked={wheelchair === 'needed'}
                disabled={price.kind === 'not_online'}
                aria-describedby="pass-wheelchair-price"
                onChange={() => setWheelchair('needed')}
              />
              <span className="font-medium">
                Need a wheelchair
                <span
                  id="pass-wheelchair-price"
                  className="block text-[12px] font-normal text-ink-3"
                >
                  {price.kind === 'priced' &&
                    (price.pesewas === 0
                      ? 'Free'
                      : `${formatPesewas(price.pesewas)}`)}
                  {price.kind === 'loading' && 'Checking the price…'}
                  {price.kind === 'unknown' &&
                    'The price is shown when you review the fare.'}
                  {price.kind === 'not_online' && (
                    <>
                      Wheelchairs can&apos;t be booked online yet — call the
                      transport desk
                      <Desk />.
                    </>
                  )}
                </span>
              </span>
            </label>
            {usesOxygen && (
              <label className={choice}>
                <input
                  type="checkbox"
                  className={`${control} mt-0.5`}
                  checked={ownOxygen}
                  onChange={(e) => setOwnOxygen(e.target.checked)}
                />
                <span className="font-medium">
                  Own portable oxygen
                  <span className="block text-[12px] font-normal text-ink-3">
                    Required: a passenger who uses oxygen brings their own.
                  </span>
                </span>
              </label>
            )}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className={legend}>Who travels with them?</legend>
          <div className="mt-2 flex flex-col gap-2">
            <label className={choice}>
              <input
                type="radio"
                name="companion"
                className={`${control} mt-0.5`}
                checked={companion === 'none'}
                onChange={() => setCompanion('none')}
              />
              <span className="font-medium">No one</span>
            </label>
            <label className={choice}>
              <input
                type="radio"
                name="companion"
                className={`${control} mt-0.5`}
                checked={companion === 'caregiver'}
                disabled={seats < 1}
                onChange={() => setCompanion('caregiver')}
              />
              <span className="font-medium">
                Family or caregiver
                <span className="block text-[12px] font-normal text-ink-3">
                  {seats < 1 ? (
                    <>
                      Ask the transport desk
                      <Desk />.
                    </>
                  ) : (
                    `Free, up to ${seats} ${seats === 1 ? 'seat' : 'seats'}.`
                  )}
                </span>
              </span>
            </label>
            <label className={choice}>
              <input
                type="radio"
                name="companion"
                className={`${control} mt-0.5`}
                checked={false}
                disabled
                onChange={() => undefined}
              />
              <span className="font-medium">
                Nurse escort
                <span className="block text-[12px] font-normal text-ink-3">
                  Ask the transport desk
                  <Desk />.
                </span>
              </span>
            </label>
          </div>
          {caregiverMissing && (
            <p role="alert" className="text-[12.5px] text-sos">
              {tooYoung
                ? `A passenger under ${minAge} must travel with an adult (family or caregiver).`
                : 'This service needs a family member or caregiver to travel.'}{' '}
              {seats < 1 ? (
                <>
                  We can&apos;t book their seat online: call the transport desk
                  <Desk />.
                </>
              ) : (
                !tooYoung &&
                'Choose them, or go back and choose Patient Transport.'
              )}
            </p>
          )}
        </fieldset>

        <label className={label}>
          Notes for the driver (optional)
          <textarea
            className={field}
            rows={3}
            maxLength={300}
            value={notes}
            aria-describedby="pass-notes-help"
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <p id="pass-notes-help" className="-mt-3 text-[12px] text-ink-3">
          Access details only. Please don&apos;t add medical information — the
          hospital has the passenger&apos;s records.
        </p>

        <p
          id="pass-missing"
          className="min-h-[1.25rem] text-[12.5px] text-ink-2"
        >
          {missing.length > 0 && `Still needed: ${missing.join(', ')}.`}
        </p>
        <Button
          kind="primary"
          full
          disabled={!canContinue}
          aria-describedby="pass-missing"
          onClick={save}
        >
          Review fare
        </Button>

        <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12px] leading-relaxed text-ink-1">
          Medical emergency? Call{' '}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="font-semibold text-sos underline underline-offset-2"
          >
            {EMERGENCY_NUMBER}
          </a>{' '}
          — do not wait for a booking.
        </p>
      </PageBand>
    </PageShell>
  )
}

/** Saved answers back into the form, or an empty form. */
function fromDetails(d: Details | null): DetailsDraft {
  return {
    first: d?.bookerFirstName ?? '',
    last: d?.bookerLastName ?? '',
    phone: d?.bookerPhone ?? '',
    someoneElse: d ? !d.passengerIsBooker : false,
    passengerName: d?.passengerName ?? '',
    age: d?.passengerAge != null ? String(d.passengerAge) : '',
    passengerPhone: d?.passengerPhone ?? '',
    consent: d?.consentGiven ?? false,
    wheelchair: d?.wheelchairNeeded
      ? 'needed'
      : d?.ownWheelchair
        ? 'own'
        : 'none',
    ownOxygen: d?.ownOxygen ?? false,
    companion: d?.companion ?? 'none',
    notes: d?.driverNotes ?? '',
  }
}
